import { Flame, Zap, Shield, Copy, Check } from "lucide-react";
import { formatUSD } from "../data/api";
import { PriceDisplay } from "./PriceDisplay";
import { useState, useEffect } from "react";
import CONFIG from "../config";

const HERO_IMAGES = [
  "/assets/hero-bg-6.jpeg",
  "/assets/hero-bg-1.jpeg",
  "/assets/hero-bg-8.jpeg",
  "/assets/hero-bg-2.jpeg",
];

const embers = Array.from({ length: 20 }, (_, i) => ({
  id: i,
  left: `${Math.random() * 100}%`,
  delay: `${Math.random() * 4}s`,
  size: `${2 + Math.random() * 5}px`,
  duration: `${2 + Math.random() * 4}s`,
}));

export const HeroSection = ({ stats }) => {
  const [copied, setCopied] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const priceChange = stats?.price_change_24h || 0;

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 8000);
    return () => clearInterval(timer);
  }, []);

  const copyCA = () => {
    navigator.clipboard.writeText(CONFIG.TOKEN_CA);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section data-testid="hero-section" className="relative w-full min-h-screen flex items-center overflow-hidden">
      {/* Slideshow background */}
      {HERO_IMAGES.map((src, i) => (
        <div
          key={src}
          className="absolute inset-0"
          style={{
            backgroundImage: `url('${src}')`,
            backgroundSize: "cover",
            backgroundPosition: "center center",
            opacity: currentSlide === i ? 0.4 : 0,
            transform: currentSlide === i ? "scale(1.08)" : "scale(1)",
            transition: "opacity 2s ease-in-out, transform 8s ease-out",
            zIndex: 0,
          }}
        />
      ))}

      {/* Gradient overlays */}
      <div className="absolute inset-0 z-[1]" style={{
        background: "linear-gradient(to right, rgba(5,4,4,0.95) 0%, rgba(5,4,4,0.75) 35%, rgba(5,4,4,0.35) 65%, rgba(5,4,4,0.2) 100%)",
      }} />
      <div className="absolute inset-0 z-[1]" style={{
        background: "linear-gradient(to top, rgba(5,4,4,1) 0%, rgba(5,4,4,0.5) 20%, transparent 50%)",
      }} />
      <div className="absolute inset-0 z-[1]" style={{
        background: "radial-gradient(ellipse at 50% 100%, rgba(220,38,38,0.12) 0%, transparent 60%)",
      }} />

      {/* Embers */}
      <div className="absolute inset-0 z-10 overflow-hidden pointer-events-none">
        {embers.map((e) => (
          <div key={e.id} className="ember-particle" style={{ left: e.left, bottom: "5%", width: e.size, height: e.size, animationDelay: e.delay, animationDuration: e.duration }} />
        ))}
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-8 right-8 z-30 flex gap-2">
        {HERO_IMAGES.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrentSlide(i)}
            data-testid={`slide-indicator-${i}`}
            className={`w-2 h-2 transition-all duration-500 ${currentSlide === i ? "bg-fire-red w-6" : "bg-white/20 hover:bg-white/40"}`}
            style={{ borderRadius: "1px" }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-20 w-full px-4 sm:px-6 md:px-12 lg:px-20 py-12 sm:py-20">
        <div className="max-w-3xl">
          <div data-testid="hero-badge" className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 mb-6 sm:mb-8 border border-fire-dark/60 bg-fire-dark/20 backdrop-blur-sm" style={{ borderRadius: "2px" }}>
            <Flame className="w-3 h-3 text-fire-orange" />
            <span className="font-mono text-[10px] sm:text-[11px] text-fire-orange uppercase tracking-[0.2em]">Year of the Fire Horse 2026</span>
          </div>

          <h1 data-testid="hero-title" className="font-cinzel font-black uppercase tracking-[0.1em] sm:tracking-[0.15em] leading-[0.85] mb-4 sm:mb-6" style={{ fontSize: "clamp(2.2rem, 10vw, 7rem)", color: "#fff", textShadow: "0 0 40px rgba(220,38,38,0.5), 0 0 80px rgba(220,38,38,0.2), 0 2px 4px rgba(0,0,0,0.8)" }}>
            {CONFIG.TOKEN_NAME}
          </h1>

          <p data-testid="hero-subtitle" className="font-cinzel text-sm sm:text-lg md:text-xl text-fire-ember/90 uppercase tracking-[0.2em] sm:tracking-[0.4em] mb-8 sm:mb-12" style={{ textShadow: "0 0 20px rgba(245,158,11,0.3)" }}>
            {CONFIG.TOKEN_TAGLINE}
          </p>

          <div className="flex flex-wrap gap-6 sm:gap-10 mb-10 sm:mb-12">
            <div data-testid="hero-stat-price" className="flex items-center gap-3">
              <div className="p-2 border border-fire-dark/40 bg-fire-dark/20 backdrop-blur-sm" style={{ borderRadius: "2px" }}>
                <Zap className="w-4 h-4 text-fire-orange" />
              </div>
              <div>
                <p className="font-mono text-[10px] text-muted-foreground/70 uppercase tracking-[0.2em] mb-0.5">Price</p>
                <div className="flex items-center gap-2">
                  <PriceDisplay price={stats?.price_usd} className="font-mono text-lg sm:text-2xl font-bold text-foreground" />
                  {priceChange !== 0 && (
                    <span className={`font-mono text-xs ${priceChange > 0 ? "text-green-500" : "text-red-400"}`}>
                      {priceChange > 0 ? "+" : ""}{priceChange.toFixed(2)}%
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div data-testid="hero-stat-mcap" className="flex items-center gap-3">
              <div className="p-2 border border-fire-dark/40 bg-fire-dark/20 backdrop-blur-sm" style={{ borderRadius: "2px" }}>
                <Shield className="w-4 h-4 text-fire-red" />
              </div>
              <div>
                <p className="font-mono text-[10px] text-muted-foreground/70 uppercase tracking-[0.2em] mb-0.5">Market Cap</p>
                <p className="font-mono text-lg sm:text-2xl font-bold text-foreground">{formatUSD(stats?.market_cap)}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 sm:gap-4">
            <a data-testid="hero-buy-button" href={`https://dexscreener.com/solana/${CONFIG.PAIR_ADDRESS}`} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 px-5 sm:px-8 py-3 sm:py-3.5 bg-fire-red text-white font-cinzel uppercase tracking-[0.15em] sm:tracking-[0.2em] text-xs sm:text-sm font-bold border border-fire-red hover:bg-fire-red/80 hover:shadow-[0_0_30px_rgba(220,38,38,0.4)] transition-all duration-300" style={{ borderRadius: "2px" }}>
              <Flame className="w-4 h-4 group-hover:animate-fire-pulse" />Buy {CONFIG.TOKEN_NAME}
            </a>
            <a data-testid="hero-chart-button" href={`https://dexscreener.com/solana/${CONFIG.PAIR_ADDRESS}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 sm:px-8 py-3 sm:py-3.5 border border-forge-steel/60 text-muted-foreground font-cinzel uppercase tracking-[0.15em] sm:tracking-[0.2em] text-xs sm:text-sm font-bold hover:border-fire-dark hover:text-foreground backdrop-blur-sm transition-all duration-300" style={{ borderRadius: "2px" }}>
              View Chart
            </a>
          </div>

          <button data-testid="copy-ca-button" onClick={copyCA} className="mt-6 sm:mt-8 flex items-center gap-2 group cursor-pointer bg-transparent border-0 p-0">
            <span className="font-mono text-[10px] text-muted-foreground/40 uppercase tracking-[0.15em]">CA:</span>
            <span className="font-mono text-[10px] sm:text-[11px] text-muted-foreground/50 group-hover:text-fire-orange transition-colors duration-200 truncate max-w-[200px] sm:max-w-none">{CONFIG.TOKEN_CA}</span>
            {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3 text-muted-foreground/25 group-hover:text-fire-orange transition-colors duration-200" />}
          </button>
        </div>
      </div>
    </section>
  );
};
